import type { Recipe, RecipeWriteInput } from '@recipes/contracts';

/**
 * Client-side projection of a Recipe read model onto the write model, used by the editor,
 * the duplicate/variation flows and the version-conflict diff. Mirrors RecipesService.toWriteInput.
 */
export function recipeToWriteInput(recipe: Recipe): RecipeWriteInput {
  return {
    title: recipe.title,
    description: recipe.description,
    servings: recipe.servings,
    servingsLabel: recipe.servingsLabel,
    prepTimeMinutes: recipe.prepTimeMinutes,
    cookTimeMinutes: recipe.cookTimeMinutes,
    restTimeMinutes: recipe.restTimeMinutes,
    difficulty: recipe.difficulty,
    cuisine: recipe.cuisine,
    mealType: recipe.mealType,
    notes: recipe.notes,
    videoUrl: recipe.videoUrl ?? null,
    isBaseRecipe: recipe.isBaseRecipe,
    status: recipe.status,
    visibility: recipe.visibility,
    householdId: recipe.householdId,
    coverAssetId: recipe.cover?.assetId ?? null,
    galleryAssetIds: recipe.gallery.map((g) => g.assetId),
    ingredientSections: recipe.ingredientSections.map((section) => ({
      id: section.id,
      title: section.title,
      ingredients: section.ingredients.map((i) => ({
        id: i.id,
        quantity: i.quantity,
        quantityMax: i.quantityMax,
        quantityText: i.quantityText,
        unitCode: i.unitCode,
        unitText: i.unitText,
        name: i.name,
        note: i.note,
        optional: i.optional,
        scalable: i.scalable,
        originalText: i.originalText,
        linkedRecipeId: i.linkedRecipe?.id ?? null,
      })),
    })),
    stepSections: recipe.stepSections.map((section) => ({
      id: section.id,
      title: section.title,
      steps: section.steps.map((s) => ({
        id: s.id,
        instruction: s.instruction,
        durationSeconds: s.durationSeconds,
        ingredientRefs: s.ingredientRefs,
        linkedRecipeIds: (s.linkedRecipes ?? []).map((l) => l.id),
        imageAssetId: s.imageAssetId,
      })),
    })),
    tags: recipe.tags.map((t) => t.name),
    categoryIds: recipe.categories.map((c) => c.id),
    labels: recipe.labels.map((l) => ({
      kind: l.kind,
      value: l.value,
      source: l.source,
      confirmed: l.confirmed,
    })),
  };
}

/** All ingredients of a recipe in display order. */
export function flattenIngredients(recipe: Pick<Recipe, 'ingredientSections'>) {
  return recipe.ingredientSections.flatMap((s) => s.ingredients);
}

/** All steps of a recipe in display order, with their section title. */
export function flattenSteps(recipe: Pick<Recipe, 'stepSections'>) {
  return recipe.stepSections.flatMap((s) =>
    s.steps.map((step) => ({ ...step, sectionTitle: s.title, sectionId: s.id })),
  );
}
